import React, { PropsWithChildren, useCallback } from "react"
import { SearchForm } from "../SearchForm"
import clsx from "classnames"
import { usePageProps } from "common/hooks/data/usePageProps"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import type { AppProps } from "common/lib/getAppServerSideProps"
import { pageUrl } from "common/lib/router"
import { useRegionConfig } from "../../../../modules/i18n/hooks/useRegionConfig"
import { donationsUrl } from "../donationsUrl"

interface MobileMenuProps {
  isActive: boolean
}

export const MobileMenu = ({ isActive }: PropsWithChildren<MobileMenuProps>) => {
  const { navItems, activeRegions, pageSlug, regionCode } = usePageProps<AppProps>()
  const regionConfig = useRegionConfig()
  const { stay_informed, donations } = regionConfig
  const currentPathname = pageUrl.view({ regionCode, pageSlug })
  const t = useTranslations("layout.header")
  const {
    section_nav: { custom_nav_link: customNavLink },
  } = regionConfig
  const isCustomNavLinkActive = currentPathname === customNavLink.link

  const onRegionChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    window.location.assign(`/${event.target.value}`)
  }, [])

  const navItemsWithHome = [{ path: `/${regionCode}/home-page`, title: t("home") }, ...navItems]

  return (
    <section
      className={clsx(
        "absolute top-px mt-10 w-full overflow-hidden bg-white text-gray-800 transition-all duration-500 lg:hidden",
        {
          "h-screen overflow-y-auto": isActive,
          "h-0": !isActive,
        },
      )}
      data-testid="mobileMenu"
    >
      <div className="mx-1">
        <SearchForm />
      </div>

      <div
        className="font-sans-heading my-3 mb-8 flex flex-col text-xl font-bold"
        role="menu"
        aria-orientation="vertical"
      >
        {navItemsWithHome.map(({ path, title }) => {
          const isCurrentPage = currentPathname === path
          return (
            <a
              key={path}
              href={path}
              className={clsx("my-1.5 border-l-8 py-0.5 pl-2", {
                "border-gray-700": isCurrentPage,
                "border-white": !isCurrentPage,
              })}
              role="menuitem"
            >
              {title}
            </a>
          )
        })}
        {customNavLink.enabled ? (
          <a
            href={customNavLink.link}
            className={clsx("my-1.5 border-l-8 py-0.5 pl-2", {
              "border-gray-700": isCustomNavLinkActive,
              "border-white": !isCustomNavLinkActive,
            })}
            role="menuitem"
          >
            {customNavLink.title}
          </a>
        ) : null}
      </div>

      <div className="m-4">
        <label htmlFor="mobile-region-dropdown" className="mb-1 block text-sm font-bold">
          {t("edition")}:
        </label>
        <div className="w-full rounded-sm border border-gray-400 p-2">
          <select
            id="mobile-region-dropdown"
            value={regionCode}
            onChange={onRegionChange}
            className="w-full bg-white text-sm outline-none"
          >
            {activeRegions.map(({ code, nameInRegionLanguage }) => (
              <option value={code} key={code}>
                {nameInRegionLanguage}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="m-4 flex">
        {donations.enabled && !donations.promoted && (
          <a
            href={donationsUrl({ regionConfig, regionCode, campaign: "mobile-menu" })}
            className="min-hitbox-44 box-border inline-block max-w-40 grow items-center rounded-full bg-red-600 p-2 text-center text-xs font-bold text-white visited:text-white hover:bg-red-500 hover:text-white sm:hidden lg:px-5 lg:py-2"
          >
            {t("donate")}
          </a>
        )}

        {donations.promoted && stay_informed.enabled && (
          <a
            href={stay_informed.topbar.url}
            className="min-hitbox-44 box-border inline-block max-w-40 grow items-center rounded-full bg-black p-2 text-center text-xs font-bold text-white visited:text-white hover:bg-black hover:text-white sm:hidden lg:px-5 lg:py-2"
          >
            {t("get_newsletter")}
          </a>
        )}
      </div>
    </section>
  )
}
